import type { WireStanding } from '@/api/wire';
import { Technical, toolName } from '@/app/technical';
import { Kicker } from '@/ui/primitives';
import { standingWords } from '@/ui/standingWords';

/**
 * Where a request or a task stands, and why (ADR-0169): what the platform's
 * own rows say — who has it, how its work went, what was refused and what it
 * waits on — in words for everyone, and the sentences as recorded for a
 * superadmin (ADR-0140). Nothing here is guessed: a fact the platform does
 * not hold is not shown.
 */
export function StandingPanel({ standing, label = 'Where it stands' }: { standing: WireStanding; label?: string }) {
  const words = standingWords(standing, toolName);
  const stuck = standing.blockedBy !== null;
  return (
    <section
      className={`flex flex-col gap-2 rounded-brand border p-4 ${stuck ? 'border-warning' : 'border-line'}`}
      aria-label={label}
      data-testid="standing"
    >
      <Kicker>{label}</Kicker>
      <p className="m-0 text-body font-semibold">{words.headline}</p>
      {words.lines.length > 0 && (
        <ul className="m-0 flex list-disc flex-col gap-1 pl-5 text-body">
          {words.lines.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      )}
      {words.who && <p className="m-0 text-meta text-secondary">Who can move it: {words.who}.</p>}
      <Technical>
        <details className="text-meta">
          <summary className="min-h-[44px] cursor-pointer py-2">As recorded</summary>
          <ul className="m-0 flex flex-col gap-1 pl-5">
            {standing.checked.map((c) => (
              <li key={c}>{c}</li>
            ))}
            {standing.blockedBy && <li>Blocked by: {standing.blockedBy}</li>}
            {standing.next && <li>Next: {standing.next}</li>}
            {standing.facts.run?.error && <li>The last error: {standing.facts.run.error}</li>}
          </ul>
        </details>
      </Technical>
    </section>
  );
}
